import { useState, useEffect, useCallback, useRef } from 'react';
import * as notificationsApi from '../api/notifications';
import { useApi } from '../../../shared/hooks/useApi';
import { logError } from '../../../shared/utils/errorUtils';
import { Notification } from '../types/notifications';

// Sample notifications for demonstration
const SAMPLE_NOTIFICATIONS: Notification[] = [
  {
    id: '1',
    user_id: 'user-1',
    type: 'booking',
    title: 'New Booking Received',
    message: 'You have a new booking request for Serengeti Wildlife Explorer from John Smith. Please review and confirm.',
    is_read: false,
    created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
  },
  {
    id: '2',
    user_id: 'user-1',
    type: 'payment',
    title: 'Payment Received',
    message: 'Payment of $1,200 has been received for booking #BK-2024-001. The funds have been transferred to your account.',
    is_read: false,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
  },
  {
    id: '3',
    user_id: 'user-1',
    type: 'trip',
    title: 'Trip Status Updated',
    message: 'Your trip "Ngorongoro Crater Safari" has been confirmed and is scheduled for April 15, 2024.',
    is_read: true,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(), // 6 hours ago
  },
  {
    id: '4',
    user_id: 'user-1',
    type: 'system',
    title: 'Account Verification Complete',
    message: 'Your business account has been successfully verified. You can now access all premium features.',
    is_read: true,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
  },
  {
    id: '5',
    user_id: 'user-1',
    type: 'booking',
    title: 'Booking Cancellation',
    message: 'Booking #BK-2024-002 for "Lake Manyara Safari" has been cancelled by the customer.',
    is_read: false,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(), // 2 days ago
  },
];

/**
 * Custom hook for managing notifications
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Store API functions in refs to avoid dependency issues
  const apiRefs = useRef({
    getNotifications: notificationsApi.getNotifications,
    markAsRead: notificationsApi.markAsRead,
    markAllAsRead: notificationsApi.markAllAsRead,
    deleteNotification: notificationsApi.deleteNotification,
  });

  const { execute: fetchNotifications } = useApi(
    apiRefs.current.getNotifications
  );
  const { execute: markAsReadApi } = useApi((...args: unknown[]) =>
    apiRefs.current.markAsRead(args[0] as string)
  );
  const { execute: markAllAsReadApi } = useApi(apiRefs.current.markAllAsRead);
  const { execute: deleteNotificationApi } = useApi((...args: unknown[]) =>
    apiRefs.current.deleteNotification(args[0] as string)
  );

  // Load notifications on mount
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        setLoading(true);
        const data = await fetchNotifications();
        
        // If API returns empty data or fails, use sample notifications
        if (!data || data.length === 0) {
          setNotifications(SAMPLE_NOTIFICATIONS);
          setUnreadCount(SAMPLE_NOTIFICATIONS.filter(n => !n.is_read).length);
        } else {
          setNotifications(data);
          setUnreadCount(data.filter(n => !n.is_read).length);
        }
      } catch (error) {
        logError(error, 'loadNotifications');
        // Use sample notifications when API fails
        setNotifications(SAMPLE_NOTIFICATIONS);
        setUnreadCount(SAMPLE_NOTIFICATIONS.filter(n => !n.is_read).length);
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array since API functions are stable via refs

  // Mark a notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await markAsReadApi(notificationId);
        setNotifications(prev =>
          prev.map(n => (n.id === notificationId ? { ...n, is_read: true } : n))
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        logError(error, 'markAsRead');
        // Update local state even if API fails
        setNotifications(prev =>
          prev.map(n => (n.id === notificationId ? { ...n, is_read: true } : n))
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    },
    [markAsReadApi]
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await markAllAsReadApi();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      logError(error, 'markAllAsRead');
      // Update local state even if API fails
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }
  }, [markAllAsReadApi]);

  // Delete a notification
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      try {
        await deleteNotificationApi(notificationId);
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        // Update unread count if needed
        const wasUnread =
          notifications.find(n => n.id === notificationId)?.is_read === false;
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      } catch (error) {
        logError(error, 'deleteNotification');
        // Update local state even if API fails
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        const wasUnread =
          notifications.find(n => n.id === notificationId)?.is_read === false;
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    },
    [deleteNotificationApi, notifications]
  );

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh: fetchNotifications,
  };
}
